import { Avatar, Button, Checkbox, Flex, Form, GetProp, Input, InputNumber, List, Modal, notification, Popconfirm, Space, Spin, Tooltip, Tree, Upload, UploadProps } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { RoleModel, UserModel } from "../models/user-model";
import UsersService from "../services/users-service";

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

function UsersListPage() {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [paginatedUsers, setPaginatedUsers] =useState<UserModel[]>([]);
    const [currentPage, setCurrentPage] = useState<number|undefined>(undefined);
    const [totalUsers, setTotalUsers] = useState<number|undefined>(undefined);
    const [usersPerPage, setUsersPerPage] = useState<number>(10);
    const [userToAddOrEdit, setUserToAddOrEdit] = useState<UserModel|undefined>(undefined);
    const [newUserPassword, setNewUserPassword] = useState<string|undefined>(undefined);
    const navigate = useNavigate();
    const usersService = UsersService.getInstance();

    useEffect(()=>{
        loadUsers(1, 10);
    },[]);



    const loadUsers = (page:number, pageSize:number) => {
        setIsLoading(true);
        usersService.getUsers(page, pageSize).subscribe({
            next:(response) => {
                setCurrentPage(response.meta.current_page);
                setUsersPerPage(response.meta.per_page);
                setPaginatedUsers(response.data);
                setTotalUsers(response.meta.total)
                setIsLoading(false);
            },
            error:(error)=> {
                notification.error({message:"Error getting users"});
                setIsLoading(false);
            }
        });
    }

    return(
    <><div style={{display:'flex',flexDirection:'column',height:'100%'}}>
        <div style={{display:'flex',flexDirection:'row',width:'100%'}}>
            <h2>Users list</h2>
            <div style={{flex:1}}></div>
            <div style={{alignContent:'center'}}>
                <Button type="primary" size="large" onClick={()=>setUserToAddOrEdit({} as UserModel)} icon={<PlusOutlined />}>Add user</Button>
            </div>
        </div>
        
        <List 
            style={{flex:1,display:'flex',flexDirection:'column',height:`calc(100% - 200px)`}}
            pagination={{
                current:currentPage,
                total:totalUsers,
                pageSize:usersPerPage,
                onChange:(page, pageSize)=> {
                    loadUsers(page, pageSize);
                }
            }} 
            dataSource={paginatedUsers}
            renderItem={(item, index) => (
                <List.Item style={{alignItems:'start'}} extra={[
                    <Popconfirm title='Are you sure?' key='deleteButton' onConfirm={()=> {
                        setIsLoading(true);
                        usersService.deleteUser(item.id).subscribe({
                            next:()=>{
                                loadUsers(1, usersPerPage);
                                notification.success({message:"User deleted."})
                            },
                            error:()=>{
                                setIsLoading(false);
                                notification.error({message:"Error deleting user"});
                            }
                        });
                    }}><Button type="link" icon={<CloseOutlined />}></Button></Popconfirm>,
                ]}>
                    <List.Item.Meta
                        avatar={<Avatar shape="square" style={{width:50,height:50, cursor:'pointer'}} onClick={()=>setUserToAddOrEdit({...item} as UserModel)}>{item.id}</Avatar>}
                        title={<a onClick={()=>setUserToAddOrEdit({...item} as UserModel)}>{item.name}, email: {item.email}</a>}
                        description={'Roles: ' + item.roles.map(r=>r.name).join(', ')}
                    />
                </List.Item>
            )}>
        </List>
    </div>

    <Modal 
        closable={false}
        open={userToAddOrEdit !== undefined} 
        onCancel={()=>setUserToAddOrEdit(undefined)} 
        okButtonProps={{loading:isLoading, disabled:!userToAddOrEdit || !userToAddOrEdit.name || !userToAddOrEdit.email || (userToAddOrEdit && !userToAddOrEdit.roles)}}
        onOk={()=>{
            if (!userToAddOrEdit || !userToAddOrEdit.name || !userToAddOrEdit.email) return;
            setIsLoading(true);
            let actionName = "added";
            let action =usersService.createUser(userToAddOrEdit?.name, userToAddOrEdit?.email, newUserPassword!, userToAddOrEdit.roles.map(r=>r.name));
            if (userToAddOrEdit.id) {
                actionName = "updated";
                action = usersService.updateUser(userToAddOrEdit.id, userToAddOrEdit?.name, userToAddOrEdit?.email, userToAddOrEdit.roles.map(r=>r.name));
            }
            action.subscribe({
                next:(addedScenario)=> {
                    notification.success({message:`User ${actionName}.`, description:addedScenario.email});
                    setUserToAddOrEdit(undefined);
                    loadUsers(1, usersPerPage);
                },
                error:(error) => {
                    setIsLoading(false);
                    notification.error({message:`User not ${actionName}.`});
                }
            });
        }}
    >
        {userToAddOrEdit && <Form layout="vertical">
            <Form.Item
                label="Email"
                required={true}
                validateStatus={!userToAddOrEdit.email ? 'error' : 'success'}
                help={!userToAddOrEdit.email ? 'The field is required':''}
            >
                {userToAddOrEdit.id === undefined ? <Input value={userToAddOrEdit.email} onChange={(e)=>setUserToAddOrEdit({...userToAddOrEdit, email:e.target.value})} placeholder="some@email.com"/> : <span>{userToAddOrEdit.email}</span>}
            </Form.Item>
            <Form.Item
                label="Name"
                required={true}
                validateStatus={!userToAddOrEdit.name ? 'error' : 'success'}
                help={!userToAddOrEdit.name ? 'The field is required':''}
            >
                <Input value={userToAddOrEdit.name} onChange={(e)=>setUserToAddOrEdit({...userToAddOrEdit, name:e.target.value})} placeholder="User User"/>
            </Form.Item>
            {!userToAddOrEdit.id && <Form.Item
                label="Password"
                required={true}
                validateStatus={!newUserPassword ? 'error' : 'success'}
                help={!newUserPassword ? 'The field is required':''}
            >
                <Input.Password value={newUserPassword} onChange={(e)=>setNewUserPassword(e.target.value)} placeholder="Password"/>
            </Form.Item>}
            <Form.Item
                label="Roles"
                required={true}
                validateStatus={(!userToAddOrEdit.roles || !userToAddOrEdit.roles.length) ? 'error' : 'success'}
                help={(!userToAddOrEdit.roles || !userToAddOrEdit.roles.length) ? 'The field is required':''}
            >
                
                <Checkbox.Group value={userToAddOrEdit.roles ? userToAddOrEdit.roles.map(r=>r.name):[]} onChange={(checkedValues:string[]) => {
                    setUserToAddOrEdit({...userToAddOrEdit, roles:checkedValues.map(r=>{ return  {name:r} as RoleModel})});
                }}>
                    <Checkbox value={'admin'}>Administrator</Checkbox>
                    <Checkbox value={'user'}>User</Checkbox>
                </Checkbox.Group>
            </Form.Item>
        </Form>}
    </Modal>
    </>);
}

export default UsersListPage;